import _ from 'lodash'
import { pointers } from '@wix/thunderbolt-becky-types'
import { getContextFromInflatedId } from '../../../byRefUtils'
import {
	LAYOUT_INFLATED_MAP,
	PRESET_RESOLVING_DATA_ITEM_TYPE,
	REF_COMP_ID_TO_TEMPLATE_KEY,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
} from '../../../constants'
import { resolveOverrideItem } from '../../dataConsumption'
import { getInflatedDataItemsMap } from '../../presetsResolving'
import type { DataRefsForPresetResolving } from '../../types'
import type { DependenciesData } from './types'
import { createConsumptionDataNode, getDataRefsForPresetResolving } from './utils/dataNodeUtils'
import { createRef } from '@wix/materializer'
import type { Ref } from '@wix/thunderbolt-types'
import { getRefCompIdFromInflatedId } from '../../../../utils'
import { RawModel } from '../../../../types'
import { LayoutRefArray } from '@wix/thunderbolt-catharsis'

type Dependencies = DataRefsForPresetResolving & { shouldRenderVariableName: Ref<boolean> }

export const LayoutData = createConsumptionDataNode<DependenciesData, Dependencies, Dependencies>({
	getDependencies: (data) => ({
		...getDataRefsForPresetResolving(data),
		shouldRenderVariableName: createRef(
			[
				SITE_NAMESPACE,
				pointers.data.DATA_MAPS.DATA,
				`dataItem-${getRefCompIdFromInflatedId(data.inflatedDataItemId)}`,
				'isBuilderComponent',
			],
			false
		),
	}),
	updateDependencies: ({ update, inflatedComponent, dependencies }) => {
		update(RESOLVED_REFS_NAMESPACE, LAYOUT_INFLATED_MAP, inflatedComponent.layoutQuery, dependencies, dependencies)
	},
	toViewItem: ({ id, value, rawModel }) => {
		const { refArraysWithOverrides, shouldRenderVariableName } = value
		const templateDataItem = _.last(refArraysWithOverrides)
		if (!templateDataItem) {
			return
		}

		if (templateDataItem.type !== 'RefArray') {
			return { [id]: resolveOverrideItem(refArraysWithOverrides, id) }
		}

		const mobileVariantRelationItem = getMobileVariantRelationItem(templateDataItem as LayoutRefArray)

		// In case of old classic widget, we need to add a mobile variant to variants array because it's not inside variants_data
		// DM are not able to add this variant to variants_data since it's defined only once in master page
		if (mobileVariantRelationItem?.type === 'VariantRelation') {
			mobileVariantRelationItem.variants = [
				{
					type: 'Mobile',
					id: 'MOBILE-VARIANT',
					componentId: 'masterPage',
				},
			]
		}

		const pageVariants = _.get(rawModel, [SITE_NAMESPACE, pointers.data.DATA_MAPS.VARIANTS], {})
		const inflatedDataItemsMap = getInflatedDataItemsMap(
			value,
			id,
			pageVariants,
			PRESET_RESOLVING_DATA_ITEM_TYPE[pointers.data.DATA_MAPS.LAYOUT]
		)

		const context = getContextFromInflatedId(id)
		const inflatedWidgetRootCompId = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, context])

		const masterPageVariablesIds = getMasterPageVariablesIds(rawModel)
		return _.mapValues(inflatedDataItemsMap, (dataItem) => {
			if (dataItem.type === 'SingleLayoutData' && dataItem.variableConnections) {
				const variableConnections = dataItem.variableConnections.map((connection) => {
					const variableId = connection.variableId
					if (masterPageVariablesIds.includes(`#${variableId}`)) {
						return connection
					}

					return {
						...connection,
						variableId: `${inflatedWidgetRootCompId}-${variableId}`,
						variableName: getVariableName(rawModel, inflatedWidgetRootCompId!, connection.variableId),
						shouldRenderVariableName,
					}
				})

				return {
					...dataItem,
					variableConnections,
				}
			}

			return dataItem
		})
	},
})

const getMobileVariantRelationItem = (templateDataItem: LayoutRefArray) =>
	templateDataItem.values?.find(
		(layout) =>
			layout.type === 'VariantRelation' &&
			layout.to.type === 'SingleLayoutData' &&
			layout.to.componentLayout?.type === 'ClassicComponentLayout'
	)

const getMasterPageVariablesIds = (rawModel: any) => {
	const variablesData = _.get(rawModel, ['site', 'variables'])
	const masterPageVariableQuery = _.get(rawModel, ['site', 'structure', 'masterPage', 'variablesQuery'])

	return variablesData && masterPageVariableQuery ? variablesData[masterPageVariableQuery].variables : []
}

function getVariableName(rawModel: RawModel, inflatedWidgetRootCompId: string, variableId: string): string | undefined {
	const rootRefCompId = getRefCompIdFromInflatedId(inflatedWidgetRootCompId)

	const widgetNamespace = _.get(rawModel, [REF_COMP_ID_TO_TEMPLATE_KEY, rootRefCompId])
	const variables = _.get(rawModel, [widgetNamespace, pointers.data.DATA_MAPS.VARIABLES], {})

	return variables?.[variableId]?.name
}
